/**
 * Brand access levels on the invite and add-with-key forms.
 *
 * The level a brand gets when nobody chose one follows the member's role, by
 * the same rule the API applies to a brand named without a level: approve when
 * the role may approve proposals, else propose when it may propose, else read.
 */
import { ROLE_CAPABILITIES, type EmployeeRole } from '../../lib/rbac';

export type BrandLevel = 'approve' | 'propose' | 'read';

/** The part of a role this rule needs. */
export interface LevelRole {
  key: string;
  capabilities?: string[] | null;
}

export interface BrandPermissionEntry {
  slug: string;
  permission_level: BrandLevel;
}

const levelFor = (capabilities: readonly string[]): BrandLevel => {
  if (capabilities.includes('proposals.approve')) return 'approve';
  if (capabilities.includes('proposals.propose')) return 'propose';
  return 'read';
};

/**
 * The default level for `roleKey`. Uses the role's loaded capability list when
 * there is one (system roles an owner edited, custom roles), else the built-in
 * matrix for the five system roles. An unknown role with no list gets `read`,
 * the level that grants least.
 */
export function defaultBrandLevel(
  roleKey: string,
  roles: readonly LevelRole[] = []
): BrandLevel {
  const loaded = roles.find((r) => r.key === roleKey);
  if (loaded && Array.isArray(loaded.capabilities)) {
    return levelFor(loaded.capabilities);
  }
  const builtIn = ROLE_CAPABILITIES[roleKey as EmployeeRole];
  return builtIn ? levelFor(builtIn) : 'read';
}

/**
 * The picked brands after the role changed: a brand whose level was set by
 * hand keeps it, every other picked brand takes the new role's default.
 */
export function followRoleDefault(
  picked: Record<string, BrandLevel>,
  setByHand: Record<string, boolean>,
  level: BrandLevel
): Record<string, BrandLevel> {
  const next: Record<string, BrandLevel> = {};
  Object.keys(picked).forEach((slug) => {
    next[slug] = setByHand[slug] ? picked[slug] : level;
  });
  return next;
}

/**
 * The wire form of the picked brands: every brand with its level, so an
 * explicit `read` stays read instead of falling back to the role's default.
 * `undefined` when no brand is picked.
 */
export function brandPermissionsPayload(
  picked: Record<string, BrandLevel>
): BrandPermissionEntry[] | undefined {
  const entries = Object.keys(picked).map((slug) => ({
    slug,
    permission_level: picked[slug],
  }));
  return entries.length ? entries : undefined;
}

/** What both forms hand to the page on save. */
export interface NewMemberPayload<TMcpAccess> {
  email: string;
  name: string;
  role: string;
  brandPerms: Record<string, BrandLevel>;
  mcpAccess: TMcpAccess | null;
}

/** Request body for add-with-key (`POST agency/employees`). */
export function addMemberRequest<TMcpAccess>(
  payload: NewMemberPayload<TMcpAccess>
) {
  return {
    email: payload.email,
    name: payload.name,
    role: payload.role,
    shop_permissions: brandPermissionsPayload(payload.brandPerms),
    mcp_access: payload.mcpAccess ?? undefined,
  };
}

/**
 * Request body for an invitation (`POST agency/employees/invite`).
 * `replacePending` asks the API to replace a pending invitation of the same
 * address; it is only ever sent after the user chose that explicitly.
 */
export function inviteMemberRequest<TMcpAccess>(
  payload: NewMemberPayload<TMcpAccess>,
  options: { replacePending?: boolean } = {}
) {
  return {
    ...addMemberRequest(payload),
    ...(options.replacePending ? { replace_pending: true } : {}),
  };
}

/** A pending invitation the API reported instead of sending a new one. */
export interface PendingInvitation {
  reason: string;
  employee: {
    id: string;
    email?: string;
    name?: string;
    role?: string;
    invitation_token_expires_at?: string | null;
  };
}

/**
 * The pending invitation carried by a refused invite (409
 * `invitation_pending`), or null for every other error.
 */
export function pendingInvitationFrom(err: unknown): PendingInvitation | null {
  const e = (err ?? {}) as {
    status?: unknown;
    code?: unknown;
    reason?: unknown;
    employee?: { id?: unknown } | null;
  };
  if (e.status !== 409 || e.code !== 'invitation_pending') return null;
  const id = e.employee?.id;
  if (typeof id !== 'string' || !id) return null;
  return {
    reason: typeof e.reason === 'string' ? e.reason : '',
    employee: e.employee as PendingInvitation['employee'],
  };
}

/** Where a refused add or invite is shown inside its drawer. */
export interface DrawerRefusal {
  /** Shown under the email field. */
  email?: string;
  /** Shown at the foot of the drawer. */
  form?: string;
}

/**
 * Split a refused add or invite into an email-field error (422
 * `employee_already_exists`, or any `errors.email`) and a drawer-level one.
 * `existsFallback` is used when the server sends only the code.
 */
export function drawerRefusalFrom(
  err: unknown,
  existsFallback: string,
  formFallback: string
): DrawerRefusal {
  const e = (err ?? {}) as {
    code?: unknown;
    message?: unknown;
    reason?: unknown;
    fieldErrors?: { email?: unknown } | null;
  };
  const emailErrors = Array.isArray(e.fieldErrors?.email)
    ? (e.fieldErrors?.email as unknown[]).filter(
        (x): x is string => typeof x === 'string' && x.trim() !== ''
      )
    : [];
  if (emailErrors.length) return { email: emailErrors[0] };
  const exists =
    e.code === 'employee_already_exists' ||
    e.message === 'employee_already_exists';
  if (exists) {
    const msg = typeof e.message === 'string' ? e.message.trim() : '';
    return {
      email: msg && msg !== 'employee_already_exists' ? msg : existsFallback,
    };
  }
  const reason = typeof e.reason === 'string' ? e.reason.trim() : '';
  if (reason) return { form: reason };
  const msg = typeof e.message === 'string' ? e.message.trim() : '';
  return { form: msg || formFallback };
}
